import {
	Button,
	Table as DataTable,
	EmptyState,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff, Pin, PinOff, Plus, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import { ApiError, api, type CompetitorRow, type Competitors } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Hint } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, SortHeader, TableTools, useSort } from "~/modules/analytics/components/table";
import { magnitudeColor } from "~/modules/analytics/lib/bar-color";
import { readingPath } from "~/modules/analytics/lib/loop";

type Column = "domain" | "share" | "questions";

/**
 * Competitors, derived rather than declared — and split in two.
 *
 * Every other tool asks the customer to type in who they compete with, which
 * gets you the list they wish were true. This one is computed from who is
 * actually cited instead of them, which is frequently a surprise and always the
 * more useful answer.
 *
 * The surprise had a cost: one list ranked reddit.com above every product on
 * it. True, and useless as a competitor ranking — the answer to a Reddit thread
 * is to be mentioned in it, not to out-rank it. So a product and a page are two
 * tables now, and the rivals a customer names sit in the first one whether or
 * not the week cited them.
 */
export function CompetitorsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { editor } = useAccess();
	const { locale } = useI18n();
	const [query, setQuery] = useState("");
	const [busy, setBusy] = useState<string | null>(null);
	const sort = useSort<Column>("share");
	const queryClient = useQueryClient();
	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"competitors",
			{ range: filters.range, engine: filters.engine, locale: filters.locale },
		],
		queryFn: () => api.competitors(entityId, filters),
		...read,
	});
	// The list is part of the brand's settings, so the whole brand is stale
	// after it: the settings screen holds the same two fields as text.
	const list = useMutation({
		mutationFn: async ({
			domain,
			field,
			add,
		}: {
			domain: string;
			field: "excluded_domains" | "tracked_domains";
			add: boolean;
		}) => {
			const current = await api.settings(entityId);
			const was = field === "excluded_domains" ? current.entity.excluded_domains : current.entity.tracked_domains;
			const next = add ? [...new Set([...was, domain])] : was.filter((d) => d !== domain);
			await api.saveSettings(entityId, { [field]: next });
		},
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	/**
	 * Read-modify-write on the settings list rather than a route of its own:
	 * these are fields a customer can also edit as text, and two ways to change
	 * one list that do not go through the same validation is how the two drift.
	 */
	async function editList(domain: string, field: "excluded_domains" | "tracked_domains", add: boolean, said: string) {
		setBusy(domain);
		try {
			await list.mutateAsync({ domain, field, add });
			toast.success(said, field === "excluded_domains" ? { description: t("competitors.excludedHint") } : undefined);
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(null);
		}
	}

	/**
	 * Ignoring a suggestion dismisses every spelling of it. Adding one goes
	 * through the same list as Watch, so it is on the axis the moment it lands
	 * and the recorder's row drops out of the suggestions by itself.
	 */
	async function dismiss(row: Competitors["suggested"][number]) {
		setBusy(row.domain);
		try {
			await Promise.all(row.names.map((name) => api.mapName(entityId, { name, action: "dismiss" })));
			await queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
			toast.success(t("competitors.dismissed", { domain: row.domain }));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(null);
		}
	}

	function Suggested({ rows }: { rows: Competitors["suggested"] }) {
		return (
			<>
				<div className="title-row">
					<h3>{t("competitors.suggested")}</h3>
					<Hint text={t("competitors.suggestedHint")} />
				</div>
				<div className="table-scroll bleed-gutter">
					<DataTable variant="resource">
						<TableBody>
							{rows.map((row) => (
								<TableRow key={row.domain}>
									<TableCell className="wrap">
										{row.names[0]} <span className="faint">{row.domain}</span>
									</TableCell>
									<TableCell className="num faint">{t("competitors.suggestedMentions", { n: row.mentions })}</TableCell>
									<TableCell className="num">
										<div className="row-actions justify-end">
											<Button
												variant="default"
												type="button"
												disabled={busy !== null || !editor}
												onClick={() =>
													editList(row.domain, "tracked_domains", true, t("competitors.added", { domain: row.domain }))
												}
											>
												<Plus aria-hidden />
												{t("competitors.add")}
											</Button>
											<Button
												variant="default"
												type="button"
												disabled={busy !== null || !editor}
												onClick={() => dismiss(row)}
											>
												<X aria-hidden />
												{t("competitors.dismiss")}
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</DataTable>
				</div>
			</>
		);
	}

	const order = (row: CompetitorRow, column: Column) =>
		column === "share" ? row.share : column === "questions" ? row.cited_questions : (row.name ?? row.domain);

	function Table({ rows }: { rows: CompetitorRow[] }) {
		return (
			<div className="table-scroll bleed-gutter">
				<DataTable variant="resource">
					<TableHeader>
						<TableRow>
							<SortHeader className="wrap" label={t("table.domain")} column="domain" sort={sort} />
							<TableHead />
							<SortHeader className="num" label={t("competitors.share")} column="share" sort={sort} />
							<SortHeader className="num" label={t("competitors.questions")} column="questions" sort={sort} />
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.map((row) => {
							const view = { kind: "rival" as const, label: row.name ?? row.domain, domain: row.domain };
							return (
								<TableRow key={row.domain}>
									<TableCell className="wrap">
										<div className="competitor-name">
											<Drill view={view}>{row.name ?? row.domain}</Drill>
											{row.watched && (
												<span className="pill pill-quiet" title={t("competitors.watchedHint")}>
													{t("competitors.watched")}
												</span>
											)}
										</div>
									</TableCell>
									<TableCell style={{ width: "30%" }}>
										<span className="bar">
											<span style={{ width: `${Math.min(row.share, 100)}%`, background: magnitudeColor(row.share) }} />
										</span>
									</TableCell>
									<TableCell className="num mono">
										<Drill view={view}>{fmt.percent(locale, row.share, 1)}</Drill>
									</TableCell>
									<TableCell className="num mono">
										{/* Both figures open the same panel, because the panel is
										    where both are broken down — question by question,
										    their citations against yours. */}
										<Drill view={view}>{row.cited_questions}</Drill>
									</TableCell>
									<TableCell className="num">
										<div className="row-actions justify-end">
											{row.watched ? (
												<Button
													variant="default"
													type="button"
													disabled={busy !== null || !editor}
													onClick={() =>
														editList(
															row.domain,
															"tracked_domains",
															false,
															t("competitors.unwatched", { domain: row.domain }),
														)
													}
												>
													<PinOff aria-hidden />
													{t("competitors.unwatch")}
												</Button>
											) : (
												<>
													<Button
														variant="default"
														type="button"
														disabled={busy !== null || !editor}
														onClick={() =>
															editList(
																row.domain,
																"tracked_domains",
																true,
																t("competitors.watched.done", { domain: row.domain }),
															)
														}
													>
														<Pin aria-hidden />
														{t("competitors.watch")}
													</Button>
													<Button
														variant="default"
														type="button"
														disabled={busy !== null || !editor}
														onClick={() =>
															editList(
																row.domain,
																"excluded_domains",
																true,
																t("competitors.excluded", { domain: row.domain }),
															)
														}
													>
														<EyeOff aria-hidden />
														{t("competitors.exclude")}
													</Button>
												</>
											)}
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</DataTable>
			</div>
		);
	}

	return (
		<StageShell
			entityId={entityId}
			reading="competitors"
			lede={t("competitors.lede")}
			filters={filters}
			setFilters={setFilters}
		>
			<Async
				state={state}
				empty={
					<EmptyState
						size="compact"
						title={t("competitors.empty")}
						description={t("competitors.emptyBody")}
						action={<DrillLink to={readingPath(brand(entityId), "prompts")}>{t("empty.goPrompts")}</DrillLink>}
					/>
				}
			>
				{(all) => {
					const seen = (list: CompetitorRow[]) =>
						sort.apply(
							list.filter((r) => matches(query, r.domain, r.name)),
							order,
						);
					const rivals = seen(all.rivals);
					const sources = seen(all.sources);
					const total = all.rivals.length + all.sources.length;

					return (
						<>
							<TableTools
								sort={sort.config([
									{ id: "domain", label: t("table.domain") },
									{ id: "share", label: t("competitors.share") },
									{ id: "questions", label: t("competitors.questions") },
								])}
								query={query}
								onQuery={setQuery}
								shown={rivals.length + sources.length}
								total={total}
								// Named for the engine and not the range: this table is one
								// week's standings, and a file called `competitors-30d`
								// would be a thirty-day figure to whoever opened it later.
								csv={{ name: `competitors-${filters.engine || "all"}`, rows: csv(rivals, sources) }}
							/>

							{total === 0 ? (
								<EmptyState
									size="compact"
									title={t("competitors.empty")}
									description={t("competitors.emptyBody")}
									action={<DrillLink to={readingPath(brand(entityId), "prompts")}>{t("empty.goPrompts")}</DrillLink>}
								/>
							) : rivals.length + sources.length === 0 ? (
								<EmptyState size="compact" title={t("table.noMatch")} />
							) : (
								<div className="stack">
									<section className="stack">
										<div className="title-row">
											<h2>{t("competitors.rivals")}</h2>
											<Hint text={t("competitors.rivalsHint")} />
										</div>
										{rivals.length > 0 ? (
											<Table rows={rivals} />
										) : all.suggested.length === 0 ? (
											<EmptyState size="compact" title={t("competitors.noRivals")} />
										) : null}
										{all.suggested.length > 0 ? <Suggested rows={all.suggested} /> : null}
									</section>

									<section className="stack">
										<div className="title-row">
											<h2>{t("competitors.sources")}</h2>
											<Hint text={t("competitors.sourcesHint")} />
										</div>
										{sources.length === 0 ? (
											<EmptyState size="compact" title={t("competitors.noSources")} />
										) : (
											<Table rows={sources} />
										)}
									</section>
								</div>
							)}
						</>
					);
				}}
			</Async>
		</StageShell>
	);
}

function csv(rivals: CompetitorRow[], sources: CompetitorRow[]): (string | number)[][] {
	const row = (r: CompetitorRow, kind: string) => [
		kind,
		r.domain,
		r.name ?? "",
		r.share.toFixed(2),
		r.cited_questions,
		r.watched ? "yes" : "no",
	];
	return [
		["kind", "domain", "name", "share_percent", "questions_won", "named_by_you"],
		...rivals.map((r) => row(r, "rival")),
		...sources.map((r) => row(r, "source")),
	];
}
